import React, { useEffect, useLayoutEffect, forwardRef, useRef, useState, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

import './Modal.scss';

ReactModal.setAppElement('#root');

// Every open Modal.js instance used to share the exact same hardcoded overlay z-index, so
// when one dialog opens another on top of it (a confirm dialog from a form modal, "create
// new X" from a picker field, etc. — this happens throughout the app), which overlay
// actually painted on top — and therefore which one an outside click landed on and closed —
// depended on DOM/mount order rather than being guaranteed. This counter hands each modal,
// the moment it opens, a stacking position strictly above every modal already open, so a
// click outside always closes the most-recently-opened (topmost) one, then the next, etc.
let nextModalStackIndex = 0;

const Modal = forwardRef(({
  id,
  title,
  children,
  onClose,
  show,
  footer,
  headerAction,
  classSize = "",
  dialogStyle = {},
  showCloseButton = true,
  icon = null,
  iconBg = "#E6F1FB",
  iconColor = "#185FA5",
  noBodyPadding = false,
  // vertically centred in the window (Bootstrap's modal-dialog-centered); a window taller than
  // the screen still starts at the top and scrolls
  centered = true,
}, ref) => {
  useImperativeHandle(ref, () => ({
    hide: () => {
      onClose?.();
    }
  }));

  const dialogRef = useRef(null);
  const headerRef = useRef(null);
  // A ref here would never trigger the re-render needed to actually paint the new z-index —
  // mutating a ref doesn't schedule one, so the very first render's (stale, default) z-index
  // stays on the DOM forever unless something *else* happens to re-render this modal later.
  // That's exactly why this silently worked for long-lived modals (re-rendered anyway, e.g.
  // by their own data fetches) but left short-lived static ones (e.g. a plain confirm form)
  // stuck at the base z-index, underneath whatever opened before them. State fixes that.
  const [stackIndex, setStackIndex] = useState(null);
  const contentNodeRef = useRef(null);
  const outsideClickHandlerRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // react-modal's own shouldCloseOnOverlayClick can never fire here: Bootstrap's .modal
  // class (part of the "modal d-block rm-content" classNames below) forces the *content*
  // node itself to cover the whole viewport, so it eats every click before it can reach the
  // actual overlay div sitting behind it. This attaches a listener straight to the content
  // node instead, closing on anything that lands outside the visible dialog box — checked
  // via dialogRef.contains rather than an exact target match, so it isn't thrown off by
  // whatever intermediate wrapper markup react-modal/Bootstrap put between the two.
  //
  // It has to live in the contentRef callback itself, not a useEffect keyed on `show`:
  // react-modal doesn't mount the content node in the same commit `show` flips true in — its
  // own open-transition state machine mounts (and can remount) it a beat later, so a
  // useEffect keyed on `show` reliably fires and reads contentNodeRef.current before
  // react-modal has actually called this ref back, permanently missing the listener.
  const handleContentRef = (node) => {
    const previousNode = contentNodeRef.current;
    if (previousNode && outsideClickHandlerRef.current) {
      previousNode.removeEventListener('click', outsideClickHandlerRef.current);
    }
    contentNodeRef.current = node;
    outsideClickHandlerRef.current = null;
    if (node) {
      const handler = (e) => {
        if (!dialogRef.current?.contains(e.target)) onCloseRef.current?.();
      };
      outsideClickHandlerRef.current = handler;
      node.addEventListener('click', handler);
    }
  };

  // Runs before paint (unlike useEffect) so a modal opened on top of another never briefly
  // renders at the base z-index first — it already has its final stacking position by the
  // time the browser shows this frame.
  useLayoutEffect(() => {
    if (show) {
      setStackIndex(prev => {
        if (prev !== null) return prev;
        nextModalStackIndex += 1;
        return nextModalStackIndex;
      });
    } else {
      setStackIndex(null);
    }
  }, [show]);

  useEffect(() => {
  if (!show) return;

  const timer = setTimeout(() => {
    if (!dialogRef.current || !headerRef.current) return;

    const dialog = dialogRef.current;
    const header = headerRef.current;

    let isDragging = false;
    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let currentY = 0;

    const onMouseDown = (e) => {
      e.preventDefault();
      isDragging = true;
      startX = e.clientX - currentX;
      startY = e.clientY - currentY;
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      header.style.cursor = 'grabbing';
    };

    const onMouseMove = (e) => {
      if (!isDragging) return;
      const rect = dialog.getBoundingClientRect();
      const rawX = e.clientX - startX;
      const rawY = e.clientY - startY;
      // clamp so the dialog stays fully inside the viewport
      const minX = currentX - rect.left;
      const maxX = currentX + (window.innerWidth - rect.right);
      const minY = currentY - rect.top;
      const maxY = currentY + (window.innerHeight - rect.bottom);
      currentX = Math.min(Math.max(rawX, minX), maxX);
      currentY = Math.min(Math.max(rawY, minY), maxY);
      dialog.style.transform = `translate(${currentX}px, ${currentY}px)`;
    };

    const onMouseUp = () => {
      isDragging = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      header.style.cursor = 'grab';
    };

    header.style.cursor = 'grab';
    header.addEventListener('mousedown', onMouseDown);

    return () => {
      header.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, 0);

  return () => clearTimeout(timer);
}, [show]);


  const customStyles = {
    overlay: {
      zIndex: 1050 + (stackIndex ?? 0) * 10,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    content: {
      position: 'relative',
      inset: 'unset',
      padding: 0,
      border: 'none',
      background: 'none',
    },
  };

  return (
    <ReactModal
      isOpen={show}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick
      contentRef={handleContentRef}
      style={customStyles}
      className={{
        base: 'modal d-block rm-content',
        afterOpen: 'rm-content--after-open',
        beforeClose: 'rm-content--before-close',
      }}
      overlayClassName={{
        base: 'rm-overlay',
        afterOpen: 'rm-overlay--after-open',
        beforeClose: 'rm-overlay--before-close',
      }}
      closeTimeoutMS={300}
      contentLabel={title || ""}
    >
      <div
        className={`modal-dialog ${centered ? 'modal-dialog-centered' : ''} ${classSize}`}
        ref={dialogRef}
        style={{ position: 'relative', cursor: 'default', ...dialogStyle }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          {title && (
            <div className="modal-header" ref={headerRef}>
              <div className="modal-header__inner">
                {icon && (
                  <div className="modal-header__icon" style={{ background: iconBg, color: iconColor }}>
                    <FontAwesomeIcon icon={icon} />
                  </div>
                )}
                <h5 className="modal-title">{title}</h5>
              </div>
              {headerAction && <div className="modal-header__action">{headerAction}</div>}
              {showCloseButton && (
                <button className="modal-close ms-auto" onClick={onClose}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
          )}
          <div className={`modal-body${noBodyPadding ? ' modal-body--no-padding' : ''}`}>{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </ReactModal>
  );
});

Modal.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  children: PropTypes.node,
  onClose: PropTypes.func,
  show: PropTypes.bool.isRequired,
  footer: PropTypes.node,
  classSize: PropTypes.string,
};

export default Modal;
